import { config, useTransition } from "@react-spring/web";
import { HaNRoomInformationProps } from "./types";

import { HaNModalHeader } from "../HaNModalHeader";
import { RoomInformationWrapper, StyledBody, StyledMapWrapper } from "./styles";
import { useTranslation } from "react-i18next";
import { useAppSelector, useBreakPoint } from "@hooks";
import { MeetingRoom, RoomResource, getFloorHasMap } from "@lib/store";
import { HaNCardBottom } from "src/components/organisms/hereAndNow/HaNCardBottom/HaNCardBottom";
import { MeetingRoomActivities } from "src/components/organisms/meetingRoom/MeetingRoomActivities/MeetingRoomActivities";
import { MeetingRoomEquipment } from "src/components/organisms/meetingRoom/MeetingRoomEquipment/MeetingRoomEquipment";
import { MeetingRoomImage } from "src/components/organisms/meetingRoom/MeetingRoomImage/MeetingRoomImage";
import { MeetingRoomInfo } from "src/components/organisms/meetingRoom/MeetingRoomInfo/MeetingRoomInfo";
import { MeetingRoomMap } from "src/components/organisms/meetingRoom/MeetingRoomMap/MeetingRoomMap";
import { MeetingRoomData } from "src/components/organisms/meetingRoom/MeetingRoomData";
import { FlexCol } from "@quarks";
import { HaNControlsSection } from "../HaNControlsSection";
import { useRoomScheduleStatusForRequest, useRoomSensorStatusForRequest } from "@lib/logic";
import { LocationBasedServices } from "../../LocationBasedServices";
import { useActiveMeetingRoomRequest } from "src/components/pages/HereAndNow/Context/hooks/useActiveMeetingRoomRequest";

/**
 * Displayed in modal.
 * Shows information about room.
 */

const isResource = (room: MeetingRoom | RoomResource): room is RoomResource => {
  return "data" in room;
};

export const HaNRoomInformation = ({
  onBack,
  onClose,
  onBookNowCallback,
  room,
  page,
  setPage,
  duration = 0,
  endDateTime,
  startDateTime,
  showMapTile = "mobile_only",
}: HaNRoomInformationProps) => {
  const displayName = isResource(room) ? room.name : room.displayName;
  const roomPictureHash = isResource(room) ? (room.data.roomPictureHash ?? "") : room.roomPictureHash;
  const email = isResource(room) ? (room.data.email ?? "") : room.email;
  const capacity = isResource(room) ? room.data.capacity : room.capacity;
  const activities = isResource(room) ? room.data.activities : room.activities;
  const equipment = isResource(room) ? room.data.equipment : room.equipment;

  const { buildingId, id } = room;
  const { t } = useTranslation();
  const breakPoint = useBreakPoint();
  const activeRequest = useActiveMeetingRoomRequest();

  const scheduleStatus = useRoomScheduleStatusForRequest(activeRequest, id, email);
  const sensorStatus = useRoomSensorStatusForRequest(activeRequest, id);
  const floorHasMap = useAppSelector(getFloorHasMap(room.floorId));
  const showMap =
    (floorHasMap && breakPoint === "small" && showMapTile === "mobile_only") ||
    (floorHasMap && showMapTile === "always");

  const transition = useTransition(page === "roomInformation", {
    initial: { transform: "translate3d(0%, 0, 0)" },
    from: { transform: "translate3d(-100%, 0, 0)" },
    enter: { transform: "translate3d(0%, 0, 0)" },
    leave: { transform: "translate3d(-100%, 0, 0)" },
    config: { ...config.stiff, clamp: true },
  });

  if (!room) return <></>;

  return transition(
    (style, item) =>
      item && (
        <RoomInformationWrapper style={style}>
          <HaNModalHeader
            onBack={onBack}
            onClose={onClose}
            title={t("screen:MeetingRoomDetails")}
          />
          <StyledBody as="section">
            <MeetingRoomInfo
              roomName={displayName}
              buildingName={room.buildingName}
              floorName={room.floorName}
              capacity={capacity}
            />
            <FlexCol>
              <MeetingRoomImage
                displayName={displayName}
                email={email}
                roomPictureHash={roomPictureHash}
                buildingId={buildingId}
              />
            </FlexCol>
            <MeetingRoomData
              sensorStatus={sensorStatus}
              scheduleStatus={scheduleStatus}
              onLearnMorePress={() => setPage("sensorInformation")}
            />
            <MeetingRoomActivities activities={activities} />
            <MeetingRoomEquipment equipment={equipment} />

            <HaNControlsSection nodeId={room.id} />

            {showMap && (
              <StyledMapWrapper>
                <MeetingRoomMap room={room} />
              </StyledMapWrapper>
            )}

            <LocationBasedServices
              buildingId={buildingId}
              locationType="MeetingRoom"
              locationName={displayName}
            />
          </StyledBody>

          <HaNCardBottom
            duration={duration}
            endDateTime={endDateTime}
            onBookNowCallback={onBookNowCallback}
            room={room}
            setPage={setPage}
            startDateTime={startDateTime}
            className="py-4"
          />
        </RoomInformationWrapper>
      ),
  );
};
